import { OoxmlRange } from "@common/utils/redline/ooxml-range";
import { WORDPROCESSINGML_NS, elementNameWithoutPrefix } from "@common/utils/redline/ooxml/ooxml-names";
import { ParagraphModel } from "@common/utils/redline/ooxml/paragraph-model";
import { PROPOSED_REVISIONS_AUTHOR, ProposedRevisions } from "@common/utils/redline/ooxml/proposed-revisions";
import { Revision } from "@common/utils/redline/ooxml/revision";
import { TableStructure, type TableRowStructure, type TableStructureEntry } from "@common/utils/redline/ooxml/table-structure";
import { WordTokenizer } from "@common/utils/redline/text/word-tokenizer";
import type { RedlineTextParagraph, RedlineTextProposal } from "@common/utils/redline/wire/redline-payload";
import type { TableRowRevisionState } from "@common/utils/redline/wire/table-cell-path";

import type { EditableTextOrigin } from "./editable-paragraph-model";

export type EditableTextTarget =
    | { kind: "paragraph"; index: number }
    | { kind: "insertion"; index: number }
    | { kind: "rowInsertionCell"; insertionIndex: number; rowIndex: number; cellIndex: number }
    | { kind: "tableInsertionCell"; insertionIndex: number; rowIndex: number; cellIndex: number };

export interface EditableParagraphBlock {
    kind: "paragraph";
    text: string;
    textBeforeChanges: string;
    target: EditableTextTarget | null;
}

export interface EditableTableCell {
    columnSpan: number;
    blocks: EditableBlock[];
}

export interface RowDeletionRef {
    rowOfBookmark: string;
}

export interface EditableTableRow {
    revision: TableRowRevisionState;
    deletion: RowDeletionRef | null;
    cells: EditableTableCell[];
}

export interface EditableTableBlock {
    kind: "table";
    rows: EditableTableRow[];
}

export type EditableBlock = EditableParagraphBlock | EditableTableBlock;

interface InsertedRowTarget {
    kind: "rowInsertionCell" | "tableInsertionCell";
    insertionIndex: number;
    rowIndex: number;
    cellCount: number;
}

interface UnmatchedCardText {
    target: EditableTextTarget;
    collapsedText: string;
}

interface StructureMaps {
    targets: Map<Element, EditableTextTarget>;
    deletionsByRow: Map<Element, RowDeletionRef>;
}

interface PackageStructure {
    paragraphs: Element[];
    body: Element;
    structure: TableStructure;
    paragraphByBookmark: Map<string, Element>;
    targets: Map<Element, EditableTextTarget>;
}

const WRAPPER_ELEMENT_NAMES = new Set(["sdt", "sdtContent", "customXml", "ins", "del", "moveTo", "moveFrom", "smartTag"]);

export function textOfTarget(card: RedlineTextProposal, target: EditableTextTarget): string {
    switch (target.kind) {
        case "paragraph":
            return card.paragraphs[target.index].textProposed;
        case "insertion":
            return card.insertions[target.index].textProposed;
        case "rowInsertionCell":
            return card.tableChanges.rowInsertions[target.insertionIndex].rows[target.rowIndex][target.cellIndex];
        case "tableInsertionCell":
            return card.tableChanges.tableInsertions[target.insertionIndex].rows[target.rowIndex][target.cellIndex];
    }
}

export function originOfTarget(proposal: RedlineTextProposal, target: EditableTextTarget): EditableTextOrigin {
    if (target.kind !== "paragraph") return { kind: "netNew", engineText: textOfTarget(proposal, target) };
    const paragraph = proposal.paragraphs[target.index];
    if (paragraph.textWhenProposed === paragraph.textProposed) return { kind: "untouched", documentText: paragraph.textWhenProposed };
    return { kind: "revised", documentText: paragraph.textWhenProposed, engineText: paragraph.textProposed };
}

function withCellText(rows: string[][], rowIndex: number, cellIndex: number, text: string): string[][] {
    return rows.map((row, atRow) => (atRow === rowIndex ? row.map((cell, atCell) => (atCell === cellIndex ? text : cell)) : row));
}

export function withTargetText(card: RedlineTextProposal, target: EditableTextTarget, text: string): RedlineTextProposal {
    switch (target.kind) {
        case "paragraph":
            return { ...card, paragraphs: card.paragraphs.map((paragraph, index) => (index === target.index ? { ...paragraph, textProposed: text } : paragraph)) };
        case "insertion":
            return { ...card, insertions: card.insertions.map((insertion, index) => (index === target.index ? { ...insertion, textProposed: text } : insertion)) };
        case "rowInsertionCell":
            return {
                ...card,
                tableChanges: {
                    ...card.tableChanges,
                    rowInsertions: card.tableChanges.rowInsertions.map((insertion, index) => (index === target.insertionIndex ? { ...insertion, rows: withCellText(insertion.rows, target.rowIndex, target.cellIndex, text) } : insertion)),
                },
            };
        case "tableInsertionCell":
            return {
                ...card,
                tableChanges: {
                    ...card.tableChanges,
                    tableInsertions: card.tableChanges.tableInsertions.map((insertion, index) => (index === target.insertionIndex ? { ...insertion, rows: withCellText(insertion.rows, target.rowIndex, target.cellIndex, text) } : insertion)),
                },
            };
    }
}

export function withRowDeletionRestored(card: RedlineTextProposal, deletion: RowDeletionRef): RedlineTextProposal {
    return { ...card, tableChanges: { ...card.tableChanges, rowDeletions: card.tableChanges.rowDeletions.filter((rowDeletion) => rowDeletion.rowOfBookmark !== deletion.rowOfBookmark) } };
}

export function deletesRow(card: RedlineTextProposal, deletion: RowDeletionRef): boolean {
    return card.tableChanges.rowDeletions.some((rowDeletion) => rowDeletion.rowOfBookmark === deletion.rowOfBookmark);
}

export function withRowDeletionsOf(card: RedlineTextProposal, edited: RedlineTextProposal): RedlineTextProposal {
    return { ...card, tableChanges: { ...card.tableChanges, rowDeletions: edited.tableChanges.rowDeletions } };
}

export function targetKeyOf(target: EditableTextTarget): string {
    switch (target.kind) {
        case "paragraph":
            return `paragraph-${target.index}`;
        case "insertion":
            return `insertion-${target.index}`;
        case "rowInsertionCell":
            return `row-insertion-${target.insertionIndex}-${target.rowIndex}-${target.cellIndex}`;
        case "tableInsertionCell":
            return `table-insertion-${target.insertionIndex}-${target.rowIndex}-${target.cellIndex}`;
    }
}

export function targetLabelOf(target: EditableTextTarget): string {
    switch (target.kind) {
        case "paragraph":
            return `Edit paragraph ${target.index + 1}`;
        case "insertion":
            return `Edit new paragraph ${target.index + 1}`;
        case "rowInsertionCell":
            return `Edit new row ${target.rowIndex + 1} cell ${target.cellIndex + 1}`;
        case "tableInsertionCell":
            return `Edit new table ${target.insertionIndex + 1} row ${target.rowIndex + 1} cell ${target.cellIndex + 1}`;
    }
}

export function editableTargetsOf(card: RedlineTextProposal): EditableTextTarget[] {
    return [
        ...card.paragraphs.map((_, index): EditableTextTarget => ({ kind: "paragraph", index })),
        ...card.insertions.map((_, index): EditableTextTarget => ({ kind: "insertion", index })),
        ...card.tableChanges.rowInsertions.flatMap((insertion, insertionIndex) =>
            insertion.rows.flatMap((row, rowIndex) => row.map((_, cellIndex): EditableTextTarget => ({ kind: "rowInsertionCell", insertionIndex, rowIndex, cellIndex }))),
        ),
        ...card.tableChanges.tableInsertions.flatMap((insertion, insertionIndex) =>
            insertion.rows.flatMap((row, rowIndex) => row.map((_, cellIndex): EditableTextTarget => ({ kind: "tableInsertionCell", insertionIndex, rowIndex, cellIndex }))),
        ),
    ];
}

function paragraphBlock(text: string, textBeforeChanges: string, target: EditableTextTarget | null): EditableParagraphBlock {
    return { kind: "paragraph", text, textBeforeChanges, target };
}

function insertedRowsTable(rows: string[][], targetOfCell: (rowIndex: number, cellIndex: number) => EditableTextTarget, card: RedlineTextProposal): EditableTableBlock {
    return {
        kind: "table",
        rows: rows.map((row, rowIndex) => ({
            revision: "inserted",
            deletion: null,
            cells: row.map((_, cellIndex) => {
                const target = targetOfCell(rowIndex, cellIndex);
                const text = textOfTarget(card, target);
                return { columnSpan: 1, blocks: [paragraphBlock(text, text, target)] };
            }),
        })),
    };
}

function deletedRowTable(card: RedlineTextProposal, rowOfBookmark: string): EditableTableBlock {
    const anchorText = card.paragraphs.find((paragraph) => paragraph.bookmark === rowOfBookmark)?.textWhenProposed ?? rowOfBookmark;
    return { kind: "table", rows: [{ revision: "deleted", deletion: { rowOfBookmark }, cells: [{ columnSpan: 1, blocks: [paragraphBlock(anchorText, anchorText, null)] }] }] };
}

export function flatBlocksOf(card: RedlineTextProposal): EditableBlock[] {
    return [
        ...card.paragraphs.map((paragraph, index): EditableBlock => paragraphBlock(paragraph.textProposed, paragraph.textWhenProposed, { kind: "paragraph", index })),
        ...card.insertions.map((insertion, index): EditableBlock => paragraphBlock(insertion.textProposed, insertion.textProposed, { kind: "insertion", index })),
        ...card.tableChanges.rowInsertions.map((insertion, insertionIndex): EditableBlock =>
            insertedRowsTable(insertion.rows, (rowIndex, cellIndex) => ({ kind: "rowInsertionCell", insertionIndex, rowIndex, cellIndex }), card),
        ),
        ...card.tableChanges.tableInsertions.map((insertion, insertionIndex): EditableBlock =>
            insertedRowsTable(insertion.rows, (rowIndex, cellIndex) => ({ kind: "tableInsertionCell", insertionIndex, rowIndex, cellIndex }), card),
        ),
        ...card.tableChanges.rowDeletions.map((deletion): EditableBlock => deletedRowTable(card, deletion.rowOfBookmark)),
    ];
}

function rowRevisedBy(row: TableRowStructure, revision: "ins" | "del", authorKey: string): boolean {
    const rowProperties = TableStructure.childNamed(row.rowElement, "trPr");
    const marker = rowProperties === null ? null : TableStructure.childNamed(rowProperties, revision);
    const author = marker === null ? null : TableStructure.attributeOf(marker, "author");
    return author !== null && Revision.authorKey(author) === authorKey;
}

function insertedRowTargetsOf(structure: TableStructure, card: RedlineTextProposal): Map<Element, InsertedRowTarget> {
    const targets = new Map<Element, InsertedRowTarget>();
    const cardAuthorKey = Revision.authorKey(card.author);
    let nextTableInsertion = 0;
    let nextRowInsertion = { insertionIndex: 0, rowIndex: 0 };
    for (const table of structure.tableEntries) {
        const rowsInsertedByCard = table.rows.filter((row) => row.rowRevision === "inserted" && rowRevisedBy(row, "ins", cardAuthorKey));
        if (rowsInsertedByCard.length === 0) continue;
        if (rowsInsertedByCard.length === table.rows.length) {
            const insertion = card.tableChanges.tableInsertions[nextTableInsertion];
            if (insertion === undefined) continue;
            table.rows.forEach((row, rowIndex) => {
                const cells = insertion.rows[rowIndex];
                if (cells !== undefined) targets.set(row.rowElement, { kind: "tableInsertionCell", insertionIndex: nextTableInsertion, rowIndex, cellCount: cells.length });
            });
            nextTableInsertion += 1;
            continue;
        }
        for (const row of rowsInsertedByCard) {
            while (card.tableChanges.rowInsertions[nextRowInsertion.insertionIndex] !== undefined && nextRowInsertion.rowIndex >= card.tableChanges.rowInsertions[nextRowInsertion.insertionIndex].rows.length) {
                nextRowInsertion = { insertionIndex: nextRowInsertion.insertionIndex + 1, rowIndex: 0 };
            }
            const insertion = card.tableChanges.rowInsertions[nextRowInsertion.insertionIndex];
            if (insertion === undefined) break;
            targets.set(row.rowElement, { kind: "rowInsertionCell", insertionIndex: nextRowInsertion.insertionIndex, rowIndex: nextRowInsertion.rowIndex, cellCount: insertion.rows[nextRowInsertion.rowIndex].length });
            nextRowInsertion = { ...nextRowInsertion, rowIndex: nextRowInsertion.rowIndex + 1 };
        }
    }
    return targets;
}

function deletionsByRowOf(structure: TableStructure, paragraphByBookmark: Map<string, Element>, card: RedlineTextProposal): Map<Element, RowDeletionRef> {
    const deletionsByRow = new Map<Element, RowDeletionRef>();
    for (const deletion of card.tableChanges.rowDeletions) {
        const paragraph = paragraphByBookmark.get(deletion.rowOfBookmark);
        const row = paragraph === undefined ? null : structure.rowOf(paragraph);
        if (row !== null) deletionsByRow.set(row.rowElement, { rowOfBookmark: deletion.rowOfBookmark });
    }
    return deletionsByRow;
}

function firstParagraphIn(cellElement: Element): Element | null {
    return Array.from(cellElement.getElementsByTagNameNS(WORDPROCESSINGML_NS, "p")).find((paragraph) => TableStructure.nearestAncestorNamed(paragraph, "tc") === cellElement) ?? null;
}

function assignTargets(paragraphs: Element[], paragraphByBookmark: Map<string, Element>, structure: TableStructure, card: RedlineTextProposal): Map<Element, EditableTextTarget> {
    const targets = new Map<Element, EditableTextTarget>();
    const assignedParagraphIndexes = new Set<number>();
    card.paragraphs.forEach((paragraph, index) => {
        if (paragraph.bookmark === null) return;
        const element = paragraphByBookmark.get(paragraph.bookmark);
        if (element === undefined || targets.has(element)) return;
        targets.set(element, { kind: "paragraph", index });
        assignedParagraphIndexes.add(index);
    });
    const insertedRowTargets = insertedRowTargetsOf(structure, card);
    const unmatched: UnmatchedCardText[] = [
        ...card.paragraphs.flatMap((paragraph, index): UnmatchedCardText[] => (assignedParagraphIndexes.has(index) ? [] : [{ target: { kind: "paragraph", index }, collapsedText: WordTokenizer.comparisonKey(WordTokenizer.collapse(paragraph.textProposed)) }])),
        ...card.insertions.map((insertion, index): UnmatchedCardText => ({ target: { kind: "insertion", index }, collapsedText: WordTokenizer.comparisonKey(WordTokenizer.collapse(insertion.textProposed)) })),
    ];
    for (const paragraph of paragraphs) {
        if (targets.has(paragraph)) continue;
        const row = structure.rowOf(paragraph);
        const rowTarget = row === null ? undefined : insertedRowTargets.get(row.rowElement);
        if (row !== null && rowTarget !== undefined) {
            const cellElement = TableStructure.nearestAncestorNamed(paragraph, "tc");
            const cellIndex = cellElement === null ? -1 : row.cellElements.indexOf(cellElement);
            const isFirstParagraphOfCell = cellElement !== null && firstParagraphIn(cellElement) === paragraph;
            if (cellIndex >= 0 && cellIndex < rowTarget.cellCount && isFirstParagraphOfCell) {
                targets.set(paragraph, { kind: rowTarget.kind, insertionIndex: rowTarget.insertionIndex, rowIndex: rowTarget.rowIndex, cellIndex });
            }
            continue;
        }
        const collapsedText = WordTokenizer.comparisonKey(WordTokenizer.collapse(new ParagraphModel(paragraph).text));
        const matchIndex = unmatched.findIndex((entry) => entry.collapsedText === collapsedText);
        if (matchIndex < 0) continue;
        targets.set(paragraph, unmatched[matchIndex].target);
        unmatched.splice(matchIndex, 1);
    }
    return targets;
}

function childElementsOf(element: Element): Element[] {
    return Array.from(element.childNodes).filter((node): node is Element => node.nodeType === 1);
}

function columnSpanOf(structure: TableStructure, cellElement: Element): number {
    const record = structure.cellRecordOf(cellElement);
    return record === null ? 1 : record.gridColumnLast - record.gridColumnFirst + 1;
}

function tableBlockOf(entry: TableStructureEntry, structure: TableStructure, maps: StructureMaps): EditableTableBlock {
    return {
        kind: "table",
        rows: entry.rows.map((row) => ({
            revision: row.rowRevision,
            deletion: maps.deletionsByRow.get(row.rowElement) ?? null,
            cells: row.cellElements.map((cellElement) => ({ columnSpan: columnSpanOf(structure, cellElement), blocks: blocksOf(cellElement, structure, maps) })),
        })),
    };
}

function blocksOf(container: Element, structure: TableStructure, maps: StructureMaps): EditableBlock[] {
    const blocks: EditableBlock[] = [];
    for (const child of childElementsOf(container)) {
        const name = elementNameWithoutPrefix(child);
        if (name === "p") {
            const model = new ParagraphModel(child);
            blocks.push(paragraphBlock(model.text, model.allChangesRejectedText, maps.targets.get(child) ?? null));
            continue;
        }
        if (name === "tbl") {
            const entry = structure.tableEntries.find((table) => table.tableElement === child);
            if (entry !== undefined) blocks.push(tableBlockOf(entry, structure, maps));
            continue;
        }
        if (WRAPPER_ELEMENT_NAMES.has(name)) blocks.push(...blocksOf(child, structure, maps));
    }
    return blocks;
}

function packageStructureOf(proposalPackageOoxml: string, card: RedlineTextProposal): PackageStructure | null {
    let range: OoxmlRange;
    try {
        range = new OoxmlRange(proposalPackageOoxml);
    } catch {
        return null;
    }
    if (range.paragraphCount === 0) return null;
    const paragraphs = Array.from({ length: range.paragraphCount }, (_, index) => range.paragraphElement(index));
    const ownerDocument = paragraphs[0].ownerDocument;
    const body = ownerDocument.getElementsByTagNameNS(WORDPROCESSINGML_NS, "body")[0] ?? ownerDocument.documentElement;
    const structure = new TableStructure(body);
    const paragraphByBookmark = range.paragraphElementsByBookmarkName();
    const targets = assignTargets(paragraphs, paragraphByBookmark, structure, card);
    if (targets.size !== editableTargetsOf(card).length) return null;
    return { paragraphs, body, structure, paragraphByBookmark, targets };
}

export function structuredBlocksOf(proposalPackageOoxml: string, card: RedlineTextProposal): EditableBlock[] | null {
    const packageStructure = packageStructureOf(proposalPackageOoxml, card);
    if (packageStructure === null) return null;
    const { body, structure, paragraphByBookmark, targets } = packageStructure;
    const deletionsByRow = deletionsByRowOf(structure, paragraphByBookmark, card);
    if (deletionsByRow.size !== card.tableChanges.rowDeletions.length) return null;
    return blocksOf(body, structure, { targets, deletionsByRow });
}

function paragraphAsItReadsNow(paragraph: RedlineTextParagraph, liveParagraphWithCardRelabeled: ParagraphModel): RedlineTextParagraph {
    const textWithoutCard = liveParagraphWithCardRelabeled.textBeforeAuthor(PROPOSED_REVISIONS_AUTHOR);
    if (paragraph.textProposed === paragraph.textWhenProposed) return { ...paragraph, textWhenProposed: textWithoutCard, textProposed: textWithoutCard };
    return { ...paragraph, textWhenProposed: textWithoutCard, textProposed: liveParagraphWithCardRelabeled.text };
}

export function liveParagraphTextsOf(card: RedlineTextProposal, proposalPackageOoxml: string | null, proposalStampedAt: string | null): RedlineTextProposal | null {
    if (proposalPackageOoxml === null || proposalStampedAt === null) return null;
    const packageStructure = packageStructureOf(proposalPackageOoxml, card);
    if (packageStructure === null) return null;
    const rangeWithCardRelabeled = new OoxmlRange(ProposedRevisions.relabel({ packageOoxml: proposalPackageOoxml, stampedAt: proposalStampedAt }));
    const liveParagraphByCardIndex = new Map<number, ParagraphModel>();
    packageStructure.paragraphs.forEach((element, paragraphIndex) => {
        const target = packageStructure.targets.get(element);
        if (target?.kind === "paragraph") liveParagraphByCardIndex.set(target.index, new ParagraphModel(rangeWithCardRelabeled.paragraphElement(paragraphIndex)));
    });
    return {
        ...card,
        paragraphs: card.paragraphs.map((paragraph, index) => {
            const liveParagraph = liveParagraphByCardIndex.get(index);
            return liveParagraph === undefined ? paragraph : paragraphAsItReadsNow(paragraph, liveParagraph);
        }),
    };
}

export function editableBlocksOf(proposalPackageOoxml: string | null, card: RedlineTextProposal): EditableBlock[] {
    const structured = proposalPackageOoxml === null ? null : structuredBlocksOf(proposalPackageOoxml, card);
    return structured ?? flatBlocksOf(card);
}
